/**
 * IndexedDB-based photo storage service
 * Handles efficient storage and retrieval of photo blobs
 */

interface PhotoData {
  id: string;
  blob: Blob;
  base64?: string;
  uri?: string;
  type: number;
  filename: string;
  timestamp: number;
  incidentId?: number;
}

class PhotoStorageService {
  private dbName = 'ShzoomPhotoStorage';
  private version = 1;
  private storeName = 'photos';
  private db: IDBDatabase | null = null;

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('incidentId', 'incidentId', { unique: false });
          store.createIndex('type', 'type', { unique: false });
        }
      };
    });
  }

  /**
   * Ensure database is initialized
   */
  private async ensureDb(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Failed to initialize IndexedDB');
    }
    return this.db;
  }

  /**
   * Store a photo in IndexedDB
   */
  async storePhoto(photo: PhotoData): Promise<void> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      // Add timestamp if not provided
      if (!photo.timestamp) {
        photo.timestamp = Date.now();
      }
      
      const request = store.put(photo);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        console.error('Failed to store photo:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Retrieve a photo by ID
   */
  async getPhoto(id: string): Promise<PhotoData | null> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);
      
      request.onsuccess = () => {
        resolve(request.result || null);
      };
      
      request.onerror = () => {
        console.error('Failed to retrieve photo:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all photos for a specific incident
   */
  async getPhotosByIncident(incidentId: number): Promise<PhotoData[]> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('incidentId');
      const request = index.getAll(incidentId);
      
      request.onsuccess = () => {
        resolve(request.result || []);
      };
      
      request.onerror = () => {
        console.error('Failed to retrieve photos by incident:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete a photo by ID
   */
  async deletePhoto(id: string): Promise<void> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        console.error('Failed to delete photo:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete all photos for a specific incident
   */
  async deletePhotosByIncident(incidentId: number): Promise<void> {
    const photos = await this.getPhotosByIncident(incidentId);
    
    for (const photo of photos) {
      await this.deletePhoto(photo.id);
    }
  }

  /**
   * Clean up old photos (older than specified days)
   */
  async cleanupOldPhotos(daysToKeep: number = 7): Promise<number> {
    const db = await this.ensureDb();
    const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      const range = IDBKeyRange.upperBound(cutoffTime);
      const request = index.openCursor(range);
      
      let deletedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        
        if (cursor) {
          store.delete(cursor.primaryKey);
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      
      request.onerror = () => {
        console.error('Failed to cleanup old photos:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get storage size estimate
   */
  async getStorageInfo(): Promise<{ usage: number; quota: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0
      };
    }
    
    // Fallback for browsers that don't support storage.estimate
    return {
      usage: 0,
      quota: 0
    };
  }

  /**
   * Clear all photos from storage
   */
  async clearAll(): Promise<void> {
    const db = await this.ensureDb();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        console.error('Failed to clear all photos:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Convert base64 to Blob
   */
  base64ToBlob(base64: string, contentType: string = 'image/jpeg'): Blob {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  /**
   * Convert Blob to base64
   */
  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      
      reader.onerror = () => {
        reject(reader.error);
      };
      
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Store photo from base64 string
   */
  async storePhotoFromBase64(
    id: string,
    base64: string,
    type: number,
    filename: string,
    incidentId?: number
  ): Promise<void> {
    const blob = this.base64ToBlob(base64);
    
    await this.storePhoto({
      id,
      blob,
      base64,
      type,
      filename,
      timestamp: Date.now(),
      incidentId
    });
  }

  /**
   * Get photo as base64 string
   */
  async getPhotoAsBase64(id: string): Promise<string | null> {
    const photo = await this.getPhoto(id);
    
    if (!photo) {
      return null;
    }
    
    if (photo.base64) {
      return photo.base64;
    }
    
    if (photo.blob) {
      return await this.blobToBase64(photo.blob);
    }
    
    return null;
  }

  /**
   * Check if database is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      await this.ensureDb();
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const photoStorage = new PhotoStorageService();

// Export type for use in other modules
export type { PhotoData };